import axios from "axios";
import { saveAs } from "file-saver";
import {
  uploadFormDataWithProgress,
  prepareFormDataForStreaming,
} from "./streamingUpload";

const API = axios.create({
  baseURL: "/api/eval_bot",
});

// Track active upload XHR for cancellation support
let activeUploadXhr = null;

//register
/**
 * Creates a bot using the existing approach with streaming upload for files
 * @param {FormData} botData - The form data containing bot configuration and files
 * @param {String} token - Authentication token
 * @param {Object} options - Additional options
 * @param {Function} options.onProgress - Optional callback for progress updates
 * @param {Function} options.onError - Optional callback for error handling
 * @returns {Promise} - Resolves with created bot data
 */
const createBot = async (botData, token, options = {}) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  const { onProgress, onError } = options;
  try {
    // Check if formData contains any files
    let hasFiles = false;
    // Determine if we need to use streaming approach
    let useStreaming = false;

    if (botData.has("datasets")) {
      const datasets = botData.get("datasets");
      try {
        // Try to parse as JSON if it's a string
        const parsedDatasets =
          typeof datasets === "string" ? JSON.parse(datasets) : datasets;

        // Check if any dataset has filedata
        if (Array.isArray(parsedDatasets)) {
          hasFiles = parsedDatasets.some(
            (d) => d.filedata || d instanceof File
          );
        } else if (typeof parsedDatasets === "object") {
          // Handle object format (indexed object)
          hasFiles = Object.values(parsedDatasets).some(
            (d) => d.filedata || d instanceof File
          );
        }
      } catch (e) {
        console.log("Error parsing datasets:", e);
      }
    }

    // Check for file inputs directly
    for (const [key, value] of botData.entries()) {
      if (value instanceof File || (key.startsWith("file") && value)) {
        hasFiles = true;
        break;
      }
    }

    useStreaming = hasFiles;

    // Convert botData to the right format if needed
    let formData = botData;
    if (!(botData instanceof FormData)) {
      formData = prepareFormDataForStreaming(botData);
      useStreaming = true;
    }

    // Decide which approach to use
    if (useStreaming) {
      return await uploadFormDataWithProgress(
        formData,
        token,
        "/api/eval_bot/create",
        {
          onProgress,
          onError,
          setXhrRef: (xhr) => {
            activeUploadXhr = xhr;
          },
          onAbort: () => {
            activeUploadXhr = null;
          },
        }
      );
    } else {
      const response = await API.post("/create", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    }
  } catch (error) {
    console.log("error", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    return error.response.data;
  }
};

/**
 * Cancels an active bot creation upload if one exists
 */
const cancelBotUpload = () => {
  if (activeUploadXhr) {
    activeUploadXhr.abort();
    activeUploadXhr = null;
    return true;
  }
  return false;
};

//get_bot by creator
const getBotbyOrg = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  const __USER__ = JSON.parse(localStorage.getItem("user"));

  if (!token || token !== __USER__.token) {
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  let response;
  try {
    response = await API.get(`/${id}`, config);
    return response.data;
  } catch (error) {
    console.log("error", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    return [];
  }
};

//get_bot by creator templates
const getBotbyOrgTemplates = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const response = await API.get(`/templates/${id}`, config);
    return response.data;
  } catch (error) {
    console.log("error", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    return [];
  }
};

//save combination id
const saveOptimizedBot = async (ids, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const { combination_id, org_id, botID } = ids;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(
    `/save/optimized/${combination_id}/${org_id}/${botID}`,
    config
  );
  return response.data;
};

//get combinations
const getCombinations = async (sentData, token) => {
  const { id, items, from, data } = sentData;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };
  if (data?.["sortOrder"] || data?.["filters"]) {
    try {
      const response = await API.post(
        `/combinations/${id}/${items}/${from}`,
        data,
        config
      );
      return response.data;
    } catch (error) {
      console.log("error", error);
      if (error.response.status === 403) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        alert("Session Expired. Please login again.");
        window.location.href = "/";
      }
      return [];
    }
  } else {
    try {
      const response = await API.post(
        `/combinations/${id}/${items}/${from}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      console.log("error", error);
      if (error.response.status === 403) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        alert("Session Expired. Please login again.");
        window.location.href = "/";
      }
      return [];
    }
  }
};

//get combinations
const getgenerationCombinations = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(`/generation_combinations/${id}`, config);
  return response.data.combinations;
};

//get combinations
const getFinetuningCombinations = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(
    `/generation_combinations/finetune/${id}`,
    config
  );
  return response.data.combinations;
};

//run evals
const runEvals = async (ids, token) => {
  const { orgId, user_id, bot_id, combination_id } = ids;

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(
    `/runeval/retrieval/${orgId}/${user_id}/${bot_id}/${combination_id}`,
    config
  );
  return response.data;
};

//run generation evals
const runGenerationEvals = async (ids, token) => {
  const { orgId, user_id, bot_id, combination_id } = ids;

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(
    `/runeval/generation/${orgId}/${user_id}/${bot_id}/${combination_id}`,
    { retrieval_combination: ids.retrieval_combination },
    config
  );
  return response.data;
};

//run finetuning evals
const runFinetuningEvals = async (ids, token) => {
  const { orgId, user_id, bot_id, combination_id } = ids;

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(
    `/runeval/generation/finetune/${orgId}/${user_id}/${bot_id}/${combination_id}`,
    config
  );
  return response.data;
};

//run start finetuning
const startFinetuning = async (ids, token) => {
  const { orgId, user_id, bot_id } = ids;

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const response = await API.get(
    `/init/finetune/${orgId}/${user_id}/${bot_id}`,
    config
  );
  return response.data;
};

//get llm finetuning status
const getFinetuningStatus = async (ids, token) => {
  const { bot_id, combination_id } = ids;

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const response = await API.get(
      `/get/finetune/status/${bot_id}/${combination_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error: ", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
  }
};

//Generate prompts
const generatePrompts = async (bot_id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/generate/mutations/${bot_id}`, config);
    localStorage.setItem("prompts", JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    console.error("Error: ", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
  }
};

//Generate prompts
const generateQueryWithProblemStatement = async (data, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(
    `/generate/query_with_problems`,
    data,
    config
  );
  return response.data;
};

//run evals
const generateBasePrompt = async (problem_statement, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(`/generate/mutations/${""}`, config);
  localStorage.setItem("prompts", JSON.stringify(response.data));
  return response.data;
};

//run evals
const initializeCombinations = async (data, token) => {
  const { user_id, bot_id, orgId } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const response = await API.get(
      `/init/${orgId}/${user_id}/${bot_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error: ", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
  }
};

//init sync
const initializeCombinationsSync = async (data, token) => {
  const { user_id, bot_id, orgId } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/init/sync/${orgId}/${user_id}/${bot_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error: ", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
  }
};

//run evals
const initializeEmbeddingFinetuning = async (data, token) => {
  const { comb_id, bot_id, orgId } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const response = await API.post(
    `/load_embedding_ft_model/${orgId}/${bot_id}/${comb_id}`,
    config
  );
  return response.data;
};

//Initialize Mutations
const initializeMutations = async (data, token) => {
  const { bot_id } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(`/init/mutations/${bot_id}`, data, config);
  return response.data;
};

//Get Selected Combinations
const get_selected_combinations = async (bot_id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(`/selectedCombinations/${bot_id}`, config);
  return response.data;
};

//Get Selected Combinations
const makeDefault = async (data, token) => {
  const { bot_id, combination_id } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(
    `/makedefault/${bot_id}/${combination_id}`,
    config
  );
  return response.data;
};

//delete chatbot
const deleteChatbot = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
  try {
    const response = await API.delete(`/combinationset/${id}`, config);
    return response.data;
  } catch (error) {
    console.error("Error: ", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    return error;
  }
};

//delete files
const deleteFiles = async (ids, token) => {
  const { id, fileName } = ids;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
  try {
    const response = await API.delete(`/deletefile/${id}/${fileName}`, config);
    return response.data;
  } catch (error) {
    console.error("Error: ", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
  }
};

//upload sources
const uploadSources = async (data, token, options = {}) => {
  const { formData, ids } = data;
  const { orgId, user_id, bot_id } = data.ids;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  const { onProgress, onError } = options;
  const apiUrl = `/api/eval_bot/update/${orgId}/${user_id}/${bot_id}`;

  try {
    // Check if formData contains any files
    let hasFiles = false;

    // Check for file inputs directly
    for (const [key, value] of formData.entries()) {
      if (
        value instanceof File ||
        value instanceof Blob ||
        (key.startsWith("file_") && value)
      ) {
        hasFiles = true;
        break;
      }
    }

    // Decide which upload approach to use
    if (hasFiles) {
      return await uploadFormDataWithProgress(formData, token, apiUrl, {
        onProgress,
        onError,
        setXhrRef: (xhr) => {
          // Store the XHR reference to enable abort functionality
          window.activeUploadXhr = xhr;
        },
        onAbort: () => {
          window.activeUploadXhr = null;
        },
      });
    } else {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      const response = await API.post(apiUrl, formData, config);
      return response.data;
    }
  } catch (error) {
    console.log("error", error);
    if (error.response && error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }

    // If there's a response, return it; otherwise throw the error
    if (error.response) {
      return error.response.data;
    }
    throw error;
  }
};

//get data types
const getDataTypes = async (data, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(`/datatypes`, data, config);
  return response.data;
};

//get data types
const getMetadataDescription = async (data, token) => {
  const userId = data.userId;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  try {
    // Check if fileData is a proper File object
    const hasFile = data.fileData instanceof File;

    if (hasFile) {
      // Prepare FormData for streaming
      const formData = new FormData();

      // Add the file to FormData
      formData.append("file", data.fileData);

      // Add other data as JSON
      formData.append("columnTypes", JSON.stringify(data.columnTypes || {}));
      formData.append("filename", data.filename || "");

      // Use the existing uploadFormDataWithProgress function but without tracking progress
      return await uploadFormDataWithProgress(
        formData,
        token,
        `/api/eval_bot/generate-metadata-description/${userId}`
      );
    } else {
      // Fallback to regular JSON POST if no file is present

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await API.post(
        `/generate-metadata-description/${userId}`,
        JSON.stringify(data.requestData),
        config
      );

      return response.data;
    }
  } catch (error) {
    console.log("Error generating metadata:", error);
    if (error.response && error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    throw error;
  }
};

//get default combinations
const getDefaultCombinations = async (data, token) => {
  const { user_id, bot_id } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(
    `/getdefualt/${user_id}/${bot_id}`,
    data,
    config
  );
  return response.data;
};

// save default combinations
const saveDefaultCombinations = async (data, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const { bot_id, default_dict, replace, remove } = data;
  const payload = { default_dict };

  if (replace) payload.replace = replace;
  if (remove) payload.remove = remove;

  const { data: response } = await API.post(
    `/savedefault/${bot_id}`,
    payload,
    config
  );
  return response;
};

//get finetuning training loss
const getFinetuningTrainingLoss = async (data, token) => {
  const { org_id, bot_id, combination_id } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(
    `/finetune/training_loss/${org_id}/${bot_id}/${combination_id}`,
    config
  );
  return response.data;
};

//cancel finetuning training loss
const cancelFinetuning = async (data, token) => {
  const { orgId, bot_id, combination_id } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(
    `/cancel_finetuning/${orgId}/${bot_id}/${combination_id}`,
    config
  );
  return response.data;
};

//update bot name
const updateAppName = async (data, token) => {
  const { orgId, bot_id } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(
    `/edit/chatbot_name/${orgId}/${bot_id}`,
    data,
    config
  );
  return response.data;
};

//update bot name
const updateGoldenResponses = async (data, token) => {
  const { orgId, bot_id } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(
    `/update_goldenResponses/${orgId}/${bot_id}`,
    data,
    config
  );
  return response.data;
};

//update bot name
const updatePromptStatementSettings = async (data, token) => {
  const { orgId, bot_id } = data;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(
    `/update/promptStatementSettings/${orgId}/${bot_id}`,
    data,
    config
  );
  return response.data;
};

//validate neo4j credentials
const validateNeo4jCreds = async (data, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(`/validateNeo4j/`, data, config);
  return response.data;
};

// Get Combination Details
const getCombinationDetails = async (data, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const { bot_id, type, combo_id } = data;
  const response = await API.get(
    `/getCombination/${bot_id}/${type}/${combo_id}`,
    config
  );
  return response.data;
};

// generate questions
const generateQuestions = async (data, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const { bot_id, num_of_qa_pairs } = data;
  const response = await API.post(
    `/generate_golden_q_and_a/${bot_id}`,
    { num_of_qa_pairs },
    config
  );
  return response.data;
};

// get init cost
const getInitCost = async (data, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const { orgId, userId, bot_id } = data;
  const response = await API.get(
    `/init/cost_and_time/${orgId}/${userId}/${bot_id}`,
    config
  );
  return response.data;
};

// get bot by id
const getBotbyID = async (data, token) => {
  if (!token) {
    token = localStorage.getItem("token")
      ? JSON.parse(localStorage.getItem("token"))
      : null;
  }

  if (!token) {
    throw new Error("Authorization token is missing");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { bot_id } = data;

  try {
    const response = await API.post(`/botById/${bot_id}`, {}, config); // Empty object as body
    return response.data;
  } catch (error) {
    console.error("Error fetching bot by ID:", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    throw error;
  }
};

export const downloadParsedFiles = async (data, token) => {
  try {
    if (!token) {
      token = JSON.parse(localStorage.getItem("token"));
    }

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: "blob", // Important for downloading files
    };

    const { org_id, bot_id, comb_id } = data;
    const response = await API.get(
      `/get/parsed_output/${org_id}/${bot_id}/${comb_id}`,
      config
    );

    // Create a Blob from the response and trigger download
    const blob = new Blob([response.data], { type: "application/zip" });
    saveAs(blob, `parsed_files_${comb_id}.zip`);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

//get_bots by user
const getBotsbyUser = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  const __USER__ = JSON.parse(localStorage.getItem("user"));

  if (!token || token !== __USER__.token) {
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  let response;
  try {
    response = await API.get(`/get/bot_via_user/${id}`, config);
    return response.data;
  } catch (error) {
    console.log("error", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    return [];
  }
};

// get combinations eval
const getCombinationEvalBatch = async (data, token) => {
  if (!token) {
    token = localStorage.getItem("token")
      ? JSON.parse(localStorage.getItem("token"))
      : null;
  }

  if (!token) {
    throw new Error("Authorization token is missing");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(`/combinations/eval/`, data, config); // Empty object as body
    return response.data;
  } catch (error) {
    console.error("Error fetching combinations by ID:", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    throw error;
  }
};

const addCombination = async (data, token) => {
  if (!token) {
    token = localStorage.getItem("token")
      ? JSON.parse(localStorage.getItem("token"))
      : null;
  }

  if (!token) {
    throw new Error("Authorization token is missing");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/add/combination/generation/${data.bot_id}`,
      data.payload,
      config
    ); // Empty object as body
    return response.data;
  } catch (error) {
    console.error("Error fetching combinations by ID:", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    throw error;
  }
};

const updateCombination = async (data, token) => {
  if (!token) {
    token = localStorage.getItem("token")
      ? JSON.parse(localStorage.getItem("token"))
      : null;
  }

  if (!token) {
    throw new Error("Authorization token is missing");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.put(
      `/update/combination/generation/${data.bot_id}`,
      data.payload,
      config
    ); // Empty object as body
    return response.data;
  } catch (error) {
    console.error("Error fetching combinations by ID:", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    throw error;
  }
};

const chatbotService = {
  createBot,
  getBotbyOrg,
  getBotbyOrgTemplates,
  saveOptimizedBot,
  getCombinations,
  getgenerationCombinations,
  getFinetuningCombinations,
  runEvals,
  runGenerationEvals,
  runFinetuningEvals,
  startFinetuning,
  getFinetuningStatus,
  deleteChatbot,
  deleteFiles,
  initializeCombinations,
  initializeCombinationsSync,
  generatePrompts,
  generateQueryWithProblemStatement,
  initializeMutations,
  get_selected_combinations,
  makeDefault,
  uploadSources,
  getDataTypes,
  getMetadataDescription,
  initializeEmbeddingFinetuning,
  getDefaultCombinations,
  saveDefaultCombinations,
  getFinetuningTrainingLoss,
  cancelFinetuning,
  updateAppName,
  updateGoldenResponses,
  updatePromptStatementSettings,
  validateNeo4jCreds,
  getCombinationDetails,
  generateQuestions,
  getInitCost,
  getBotbyID,
  downloadParsedFiles,
  getBotsbyUser,
  getCombinationEvalBatch,
  cancelBotUpload,
  addCombination,
  updateCombination,
};

export default chatbotService;
